(() => {
  const mobileQuery = window.matchMedia('(max-width: 749px)');
  const isMobile = () => mobileQuery.matches;

  const getElements = (gallery) => {
    const mobileNavigation = gallery.querySelector('[data-up-mobile-navigation]');
    const thumbnails = gallery.querySelector('[id^="GalleryThumbnails"]');
    const thumbnailList = thumbnails?.querySelector('[id^="Slider-Thumbnails"]');
    const viewer = gallery.querySelector('[id^="GalleryViewer"]');

    return {
      mobileNavigation,
      thumbnails,
      thumbnailList,
      nativePrev: viewer?.querySelector('.slider-button--prev'),
      nativeNext: viewer?.querySelector('.slider-button--next'),
      mobilePrev: gallery.querySelector('[data-up-mobile-gallery-prev]'),
      mobileNext: gallery.querySelector('[data-up-mobile-gallery-next]'),
    };
  };

  const getThumbnailContentWidth = (thumbnailList) => {
    const items = [...thumbnailList.children].filter((item) => item.offsetParent !== null);
    if (!items.length) return 0;

    const styles = window.getComputedStyle(thumbnailList);
    const gap = Number.parseFloat(styles.columnGap || styles.gap || '0') || 0;
    const itemsWidth = items.reduce((total, item) => total + item.getBoundingClientRect().width, 0);

    return itemsWidth + gap * Math.max(0, items.length - 1);
  };

  const updateThumbnailLayout = (gallery) => {
    if (!gallery) return false;

    const { mobileNavigation, thumbnailList } = getElements(gallery);
    if (!mobileNavigation || !thumbnailList) return false;

    if (!isMobile()) {
      mobileNavigation.classList.remove('is-overflowing');
      thumbnailList.classList.remove('is-overflowing');
      return false;
    }

    mobileNavigation.classList.remove('is-overflowing');
    thumbnailList.classList.remove('is-overflowing');

    /* La largeur disponible est celle de la zone centrale située entre les deux flèches. */
    const availableWidth = thumbnailList.getBoundingClientRect().width;
    const contentWidth = getThumbnailContentWidth(thumbnailList);
    const overflowing = contentWidth > availableWidth + 1;

    mobileNavigation.classList.toggle('is-overflowing', overflowing);
    thumbnailList.classList.toggle('is-overflowing', overflowing);

    if (!overflowing) {
      thumbnailList.scrollLeft = 0;
    }

    return overflowing;
  };

  const centerActiveThumbnail = (gallery, behavior = 'auto') => {
    if (!gallery || !isMobile()) return;

    const { thumbnails, thumbnailList, mobileNavigation } = getElements(gallery);
    if (!thumbnails || thumbnails.classList.contains('small-hide') || !thumbnailList || !mobileNavigation) return;

    if (!mobileNavigation.classList.contains('is-overflowing')) return;

    const activeButton = thumbnailList.querySelector('button[aria-current="true"]');
    const activeItem = activeButton?.closest('[data-target]');
    if (!activeItem) return;

    const listRect = thumbnailList.getBoundingClientRect();
    const itemRect = activeItem.getBoundingClientRect();
    const delta = itemRect.left + itemRect.width / 2 - (listRect.left + listRect.width / 2);
    const maxScroll = Math.max(0, thumbnailList.scrollWidth - thumbnailList.clientWidth);
    const target = Math.min(Math.max(0, thumbnailList.scrollLeft + delta), maxScroll);

    thumbnailList.scrollTo({ left: target, behavior });
  };

  const syncMobileArrows = (gallery) => {
    const { nativePrev, nativeNext, mobilePrev, mobileNext } = getElements(gallery);

    if (mobilePrev) {
      const disabled = !nativePrev || nativePrev.disabled;
      mobilePrev.disabled = disabled;
      mobilePrev.setAttribute('aria-disabled', disabled ? 'true' : 'false');
    }

    if (mobileNext) {
      const disabled = !nativeNext || nativeNext.disabled;
      mobileNext.disabled = disabled;
      mobileNext.setAttribute('aria-disabled', disabled ? 'true' : 'false');
    }
  };

  const refreshMobileNavigation = (gallery, behavior = 'auto') => {
    updateThumbnailLayout(gallery);
    syncMobileArrows(gallery);
    centerActiveThumbnail(gallery, behavior);
  };

  const patchGallery = (gallery) => {
    if (!gallery || gallery.dataset.upscalePatchReady === 'true') return;

    if (gallery.dataset.preservePageScroll === 'true' && typeof gallery.setActiveMedia === 'function') {
      const originalSetActiveMedia = gallery.setActiveMedia.bind(gallery);
      gallery.setActiveMedia = (...args) => {
        const x = window.scrollX;
        const y = window.scrollY;
        originalSetActiveMedia(...args);

        requestAnimationFrame(() => window.scrollTo({ left: x, top: y, behavior: 'auto' }));
        window.setTimeout(() => window.scrollTo({ left: x, top: y, behavior: 'auto' }), 60);
      };
    }

    const { thumbnails, thumbnailList, nativePrev, nativeNext, mobilePrev, mobileNext } = getElements(gallery);

    mobilePrev?.addEventListener('click', () => nativePrev?.click());
    mobileNext?.addEventListener('click', () => nativeNext?.click());

    [nativePrev, nativeNext].forEach((button) => {
      if (!button) return;
      const observer = new MutationObserver(() => syncMobileArrows(gallery));
      observer.observe(button, { attributes: true, attributeFilter: ['disabled', 'class', 'aria-disabled'] });
    });

    if (thumbnails && thumbnailList) {
      const observer = new MutationObserver((mutations) => {
        const activeChanged = mutations.some(
          (mutation) => mutation.type === 'attributes' && mutation.attributeName === 'aria-current',
        );
        const structureChanged = mutations.some((mutation) => mutation.type === 'childList');

        if (activeChanged || structureChanged) {
          requestAnimationFrame(() => refreshMobileNavigation(gallery, activeChanged ? 'smooth' : 'auto'));
        }
      });

      observer.observe(thumbnailList, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['aria-current'],
      });
    }

    requestAnimationFrame(() => refreshMobileNavigation(gallery));
    gallery.dataset.upscalePatchReady = 'true';
  };

  const init = (root = document) => {
    root.querySelectorAll('media-gallery').forEach(patchGallery);
  };

  let resizeTimer;
  window.addEventListener('resize', () => {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => {
      document.querySelectorAll('media-gallery').forEach((gallery) => refreshMobileNavigation(gallery));
    }, 120);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
